import { FuelButton, FuelIcon, FuelTypography } from '@frete.com/fuel-react';
import { FuelBottomSheet, FuelFlex, FuelModal } from '@frete.com/fuel-station-react';
import { textConstants } from 'constants/texts';
import { useAmplitude } from 'hooks/useAmplitude';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { useDeviceSize } from 'hooks/useDeviceSize';
import { useGetTruckerQueriesData } from 'hooks/useGetTruckerQueriesData';
import { useSendTracking } from 'hooks/useSendTracking';
import { useTruckerProps } from 'hooks/useTruckerProps';
import { IPhone } from 'interfaces/profile.interface';
import React, { FC, useState } from 'react';
import { phoneMask } from 'utils';
import { PhoneContainer, Wrapper } from './styled';

const PhoneButton: FC = () => {
  const { truckerData: data } = useGetTruckerQueriesData();
  const { isMobile } = useDeviceSize();

  const [isOpen, setIsOpen] = useState(false);
  const [handleCopy] = useCopyToClipboard('Telefone');
  const [isActive, setIsActive] = useState(null);

  const { truckerId, portalName, freight, freightId, eventsSource } = useTruckerProps();
  const { sendTrackingJaiminho } = useSendTracking();
  const { sendAmplitude } = useAmplitude();

  const hasMultipleNumbers = data?.trucker?.phones?.length > 1;

  const handleModal = (): void => {
    setIsOpen(!isOpen);
    setIsActive([false]);
  };

  const handleOpenEvent = (event: React.MouseEvent): void => {
    const negotiationStartedCommomEventProps = {
      type: 'telefone',
      isVerified: data?.isVerified,
      source: eventsSource,
    };

    sendAmplitude('trucker_profile_negotiation_started', {
      ...negotiationStartedCommomEventProps,
      freightCode: freight?.code ? freight?.code : textConstants.notAvailaible,
      truckerId,
    });

    sendTrackingJaiminho(
      {
        nameEvent: 'trucker_profile_negotiation_started',
        data: {
          ...negotiationStartedCommomEventProps,
          freightId: freightId ? `hashed|${freightId}` : textConstants.notAvailaible,
          truckerId: `hashed|${truckerId}`,
        },
        clickEvent: event,
      },
      'trucker_profile'
    );
  };

  const handleCopyEvent = () => {
    sendAmplitude('trucker_profile_copy_phone_clicked', { type: 'modal-telefones' });
    sendTrackingJaiminho(
      {
        nameEvent: 'trucker_profile_copy_phone_clicked',
        data: {
          type: 'modal-telefones',
          truckerId: `hashed|${truckerId}`,
          freightId: freightId ? `hashed|${freightId}` : textConstants.notAvailaible,
          isVerified: data.isVerified,
        },
      },
      'trucker_profile'
    );
  };

  const handleClick = (event: React.MouseEvent): void => {
    handleModal();
    handleOpenEvent(event);
  };

  const handleActiveListItem = (index: number): void => {
    setIsActive(index);
    handleCopyEvent();
  };

  const handleCopyToClipboard = (phone: IPhone): void => {
    const buildPhone = `(${phone?.ddd}) ${phone?.number}`;

    handleCopy(buildPhone);
  };

  const handleCopyPhone = (phone: IPhone): void => {
    handleCopyToClipboard(phone);
    handleCopyEvent();
  };

  const formatPhone = (phone: IPhone) => phoneMask(`${phone.ddd}${phone.number}`);

  const SINGLE_PHONE_DISPLAY_TEXT = 'Ver telefone';
  const MULTIPLE_PHONE_DISPLAY_TEXT = 'Ver telefones';

  return (
    <>
      <FuelButton
        data-testid="phone-button"
        variant="secondary"
        trailingIcon="phone"
        onlyIcon
        onClick={handleClick}
        size="small"
      />

      {isMobile ? (
        <FuelBottomSheet
          portalName={portalName}
          isOpen={isOpen}
          title={hasMultipleNumbers ? MULTIPLE_PHONE_DISPLAY_TEXT : SINGLE_PHONE_DISPLAY_TEXT}
          onClose={handleModal}
          zIndex={1030}
          isChildBottom
          heightBasedOnContent
        >
          <Wrapper>
            {data?.trucker?.phones?.map((phone, index) => (
              <a href={`tel:+55${phone.ddd}${phone.number}`} key={phone.number}>
                <PhoneContainer
                  key={phone.number}
                  isActive={isActive === index}
                  onClick={() => {
                    handleActiveListItem(index);
                    handleCopyPhone(phone);
                  }}
                >
                  <FuelIcon data-testid={`phone-icon-${phone.ddd}`} name="phone" size="small" />
                  <FuelTypography data-testid={`phone-${phone.ddd}${phone.number}`} />
                  {formatPhone(phone)}
                </PhoneContainer>
              </a>
            ))}
          </Wrapper>
        </FuelBottomSheet>
      ) : (
        <FuelModal
          showCloseButton
          portalName={portalName}
          isOpen={isOpen}
          onClose={handleModal}
          width="sm"
          fixedMaxWidth
        >
          <FuelFlex defaultProps={{ flexDirection: 'column', gap: 'km6' }}>
            <FuelFlex defaultProps={{ alignItems: 'flex-start', flexDirection: 'column', gap: 'km3' }}>
              <FuelTypography tag="h2" size="heading-sm" color="neutral-strong" weight="bold">
                {hasMultipleNumbers ? MULTIPLE_PHONE_DISPLAY_TEXT : SINGLE_PHONE_DISPLAY_TEXT}
              </FuelTypography>
              <FuelTypography tag="p" size="body-sm" color="neutral-default" weight="regular">
                Telefones disponibilizados pelo motorista
              </FuelTypography>
            </FuelFlex>

            <FuelFlex defaultProps={{ flexDirection: 'column', gap: 'km4' }}>
              {data?.trucker.phones?.map((phone) => (
                <PhoneContainer key={phone.number}>
                  <FuelTypography color="neutral-default" tag="p" size="body-sm" weight="regular">
                    {formatPhone(phone)}
                  </FuelTypography>
                  <FuelButton
                    data-testid={`phone-${phone.ddd}${phone.number}`}
                    trailingIcon="duplicate"
                    variant="subtle"
                    role="button"
                    size="large"
                    onlyIcon
                    onClick={() => handleCopyPhone(phone)}
                  />
                </PhoneContainer>
              ))}
            </FuelFlex>
            <FuelFlex defaultProps={{ justifyContent: 'flex-end' }}>
              <FuelButton variant="tertiary" onClick={handleModal}>
                Voltar para o perfil do motorista
              </FuelButton>
            </FuelFlex>
          </FuelFlex>
        </FuelModal>
      )}
    </>
  );
};

export default PhoneButton;
